import { tz } from '@date-fns/tz';
import { addDays, addMonths, addWeeks, addYears, endOfDay, endOfMonth, endOfWeek, endOfYear, isSameDay, isSameMonth, isSameWeek, startOfDay, startOfHour, startOfMonth, startOfWeek, startOfYear, } from 'date-fns';
// Sunday, matching date-fns' default; get-event opts into Monday weeks.
const DEFAULT_WEEK_STARTS_ON = 0;
// The week the QR chart requests must match the week Postgres buckets.
export const QR_WEEK_STARTS_ON = 1;
/** The Monday-anchored event week. get-event's range brackets and every
 *  client-side week filter must share this anchor or edge-of-week rows
 *  silently drop. */
export const EVENT_WEEK_OPTIONS = {
    weekStartsOn: 1,
};
// @date-fns/tz yields Invalid Date (no throw) on a bad zone — validate first.
const probeIanaZone = (zone) => {
    try {
        Intl.DateTimeFormat('en-US', { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
};
const zoneValidity = new Map();
// Cached: Intl construction is too costly for per-render calls.
// "Etc/Unknown" is ICU's cannot-determine sentinel: some builds report
// it from resolvedOptions() yet accept it as input — never a real zone.
const isValidIanaZone = (zone) => {
    if (zone === 'Etc/Unknown')
        return false;
    const cached = zoneValidity.get(zone);
    if (cached !== undefined)
        return cached;
    const valid = probeIanaZone(zone);
    zoneValidity.set(zone, valid);
    return valid;
};
/** Lenient sibling of the asserting helpers for display paths:
 *  true iff the zone is a non-blank, Intl-valid IANA id. */
export const isUsableVenueTimezone = (zone) => typeof zone === 'string' && zone.trim() !== '' && isValidIanaZone(zone);
// Throws on a missing/invalid zone (no silent runtime fallback); returns it.
const assertVenueTimezone = (venueTz) => {
    if (typeof venueTz !== 'string' || venueTz.trim() === '')
        throw new Error(`venue-clock: a venue timezone is required, received ${JSON.stringify(venueTz)}`);
    if (!isValidIanaZone(venueTz))
        throw new Error(`venue-clock: "${venueTz}" is not a valid IANA timezone`);
    return venueTz;
};
// Plain Date, so toISOString() is Z-form (a TZDate's would be offset-form).
const toUtcInstant = (date) => new Date(date.getTime());
// --- Day ---------------------------------------------------------------------
/** UTC instant of the start of the venue-local calendar day. */
/** UTC instant of the start of the venue-local hour. Not the same as the UTC
 *  hour in a half-hour-offset zone (Asia/Kolkata, Asia/Kathmandu). */
export const startOfVenueHour = (instant, venueTz) => toUtcInstant(startOfHour(instant, { in: tz(assertVenueTimezone(venueTz)) }));
export const startOfVenueDay = (instant, venueTz) => toUtcInstant(startOfDay(instant, { in: tz(assertVenueTimezone(venueTz)) }));
/** UTC instant of the end of the venue-local calendar day (…T23:59:59.999). */
export const endOfVenueDay = (instant, venueTz) => toUtcInstant(endOfDay(instant, { in: tz(assertVenueTimezone(venueTz)) }));
/** UTC bracket of exactly one venue-local calendar day. */
export const venueDayInterval = (instant, venueTz) => ({
    start: startOfVenueDay(instant, venueTz),
    end: endOfVenueDay(instant, venueTz),
});
/** Whether two instants share the same venue-local calendar day. */
export const isSameVenueDay = (a, b, venueTz) => isSameDay(a, b, { in: tz(assertVenueTimezone(venueTz)) });
/** Whether `date` falls on the venue-local day `offsetDays` from `base` —
 *  the one Yesterday/Tomorrow bucketing policy for labels and pills. */
export const isVenueDayOffset = (date, base, offsetDays, venueTz) => isSameVenueDay(date, addVenuePeriods(base, 'day', offsetDays, venueTz), venueTz);
// --- Week --------------------------------------------------------------------
/** UTC instant of the start of the venue-local calendar week. */
export const startOfVenueWeek = (instant, venueTz, options) => toUtcInstant(startOfWeek(instant, {
    in: tz(assertVenueTimezone(venueTz)),
    weekStartsOn: options?.weekStartsOn ?? DEFAULT_WEEK_STARTS_ON,
}));
/** UTC instant of the end of the venue-local calendar week. */
export const endOfVenueWeek = (instant, venueTz, options) => toUtcInstant(endOfWeek(instant, {
    in: tz(assertVenueTimezone(venueTz)),
    weekStartsOn: options?.weekStartsOn ?? DEFAULT_WEEK_STARTS_ON,
}));
/** UTC bracket of exactly one venue-local calendar week. */
export const venueWeekInterval = (instant, venueTz, options) => ({
    start: startOfVenueWeek(instant, venueTz, options),
    end: endOfVenueWeek(instant, venueTz, options),
});
/** Whether two instants share the same venue-local calendar week. */
export const isSameVenueWeek = (a, b, venueTz, options) => isSameWeek(a, b, {
    in: tz(assertVenueTimezone(venueTz)),
    weekStartsOn: options?.weekStartsOn ?? DEFAULT_WEEK_STARTS_ON,
});
// --- Month -------------------------------------------------------------------
/** UTC instant of the start of the venue-local calendar month. */
export const startOfVenueMonth = (instant, venueTz) => toUtcInstant(startOfMonth(instant, { in: tz(assertVenueTimezone(venueTz)) }));
/** UTC instant of the end of the venue-local calendar month. */
export const endOfVenueMonth = (instant, venueTz) => toUtcInstant(endOfMonth(instant, { in: tz(assertVenueTimezone(venueTz)) }));
/** UTC bracket of exactly one venue-local calendar month. */
export const venueMonthInterval = (instant, venueTz) => ({
    start: startOfVenueMonth(instant, venueTz),
    end: endOfVenueMonth(instant, venueTz),
});
/** Whether two instants share the same venue-local calendar month. */
export const isSameVenueMonth = (a, b, venueTz) => isSameMonth(a, b, { in: tz(assertVenueTimezone(venueTz)) });
// --- Year --------------------------------------------------------------------
/** UTC instant of the start of the venue-local calendar year. */
export const startOfVenueYear = (instant, venueTz) => toUtcInstant(startOfYear(instant, { in: tz(assertVenueTimezone(venueTz)) }));
/** UTC instant of the end of the venue-local calendar year. */
export const endOfVenueYear = (instant, venueTz) => toUtcInstant(endOfYear(instant, { in: tz(assertVenueTimezone(venueTz)) }));
/** UTC bracket of exactly one venue-local calendar year. */
export const venueYearInterval = (instant, venueTz) => ({
    start: startOfVenueYear(instant, venueTz),
    end: endOfVenueYear(instant, venueTz),
});
const addVenuePeriodsFor = { day: addDays, week: addWeeks, month: addMonths, year: addYears };
const venueIntervalFor = {
    day: venueDayInterval,
    week: venueWeekInterval,
    month: venueMonthInterval,
    year: venueYearInterval,
};
/** Shift an instant by whole venue-local calendar periods (DST/length safe). */
export const addVenuePeriods = (instant, periodType, amount, venueTz) => toUtcInstant(addVenuePeriodsFor[periodType](instant, amount, {
    in: tz(assertVenueTimezone(venueTz)),
}));
/**
 * UTC bracket of the venue-local calendar period `offset` periods away from
 * `instant` (0 = the period containing it, -1 = the previous one). Used by
 * period-over-period analytics with prev/next navigation.
 */
export const venuePeriodInterval = (instant, periodType, offset, venueTz, options) => venueIntervalFor[periodType](addVenuePeriods(instant, periodType, offset, venueTz), venueTz, options);
//# sourceMappingURL=venue-clock.js.map